import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import {
  Package,
  Zap,
  Eye,
  EyeOff,
  Scan,
  BarChart3,
  Users,
  Cloud,
  Shield,
  Check,
  Database,
  Activity,
  Sun,
  Moon,
  Bluetooth,
  Wifi,
  Usb,
  Smartphone,
  Monitor,
  Bell,
  Radio,
  Truck,
  MapPin,
  Globe,
  Share2,
  Heart,
  ArrowRight,
  Sparkles,
  Lock,
  Server,
  CheckCircle2,
  ChevronRight,
  Award,
  TrendingUp,
  Clock,
  Layers,
  Mail
} from 'lucide-react';

export const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const [activeFeature, setActiveFeature] = useState(0);

  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 6);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          navigate(result.redirectTo || '/dashboard');
        } else {
          setError(result.error || 'Login failed');
        }
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const languages = [
    { code: 'en', flag: 'US', name: 'English' },
    { code: 'es', flag: 'ES', name: 'Espanol' },
    { code: 'fr', flag: 'FR', name: 'Francais' },
    { code: 'nl', flag: 'NL', name: 'Nederlands' }
  ];

  const features = [
    { icon: Package, titleKey: 'welcome.features.smart.title', descKey: 'welcome.features.smart.desc', color: 'from-cyan-500 to-blue-600' },
    { icon: Scan, titleKey: 'welcome.features.barcode.title', descKey: 'welcome.features.barcode.desc', color: 'from-emerald-500 to-teal-600' },
    { icon: BarChart3, titleKey: 'welcome.features.analytics.title', descKey: 'welcome.features.analytics.desc', color: 'from-blue-500 to-blue-700' },
    { icon: Users, titleKey: 'welcome.features.team.title', descKey: 'welcome.features.team.desc', color: 'from-orange-500 to-amber-600' },
    { icon: Cloud, titleKey: 'welcome.features.cloud.title', descKey: 'welcome.features.cloud.desc', color: 'from-sky-500 to-blue-600' },
    { icon: Shield, titleKey: 'welcome.features.security.title', descKey: 'welcome.features.security.desc', color: 'from-slate-500 to-slate-700' }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-50 dark:bg-[#0a0f1a]">
      {/* Premium Gradient Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-[800px] bg-gradient-to-br from-cyan-500/8 via-blue-500/5 to-transparent dark:from-cyan-500/10 dark:via-blue-600/8 dark:to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-[600px] bg-gradient-to-t from-slate-100/80 via-transparent to-transparent dark:from-slate-950/90" />

        {/* Animated Orbs */}
        <div className="absolute top-20 left-[10%] w-[500px] h-[500px] rounded-full bg-gradient-to-br from-cyan-400/20 to-blue-500/10 dark:from-cyan-500/15 dark:to-blue-600/10 blur-[100px] animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute top-[40%] right-[5%] w-[400px] h-[400px] rounded-full bg-gradient-to-br from-blue-400/15 to-cyan-500/10 dark:from-blue-500/12 dark:to-cyan-600/8 blur-[80px] animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }} />
        <div className="absolute bottom-[20%] left-[20%] w-[350px] h-[350px] rounded-full bg-gradient-to-br from-teal-400/10 to-blue-500/5 dark:from-teal-500/10 dark:to-blue-600/5 blur-[60px] animate-pulse" style={{ animationDuration: '12s', animationDelay: '4s' }} />

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] dark:bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_70%)]" />
      </div>

      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between bg-white/70 dark:bg-slate-900/70 backdrop-blur-2xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl px-4 sm:px-6 py-3 shadow-lg shadow-slate-900/5 dark:shadow-black/20">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img
                src="/ChatGPT Image Oct 8, 2025, 03_35_44 AM.png"
                alt="GetInv"
                className="h-8 sm:h-10 w-auto"
              />
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Language Selector */}
              <div className="hidden sm:flex items-center gap-1 bg-slate-100/80 dark:bg-slate-800/80 rounded-xl p-1">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code as any)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                      language === lang.code
                        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    {lang.flag}
                  </button>
                ))}
              </div>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2.5 rounded-xl bg-slate-100/80 dark:bg-slate-800/80 hover:bg-slate-200/80 dark:hover:bg-slate-700/80 transition-all duration-200"
              >
                {isDark ? (
                  <Sun className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500" />
                ) : (
                  <Moon className="h-4 w-4 sm:h-5 sm:w-5 text-slate-700" />
                )}
              </button>

              {/* CTA Button */}
              <Button
                onClick={() => setShowAuth(true)}
                variant="primary"
                className="hidden sm:flex bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white px-5 py-2.5 text-sm font-semibold shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/30 transition-all duration-300"
              >
                Sign In
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-24 lg:pb-32 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left Side - Large Logo with Balance Animation */}
              <div className="flex justify-center lg:justify-start order-2 lg:order-1">
                <div className="relative">
                  {/* Glow Effect Behind Logo */}
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/30 to-blue-600/20 rounded-full blur-[80px] scale-150" />

                  {/* Main Logo */}
                  <img
                    src="/ChatGPT Image Oct 8, 2025, 03_35_44 AM.png"
                    alt="GetInv Logo"
                    className="relative w-64 h-64 sm:w-80 sm:h-80 lg:w-[420px] lg:h-[420px] xl:w-[500px] xl:h-[500px] object-contain animate-balance"
                  />
                </div>
              </div>

              {/* Right Side - Content */}
              <div className="text-center lg:text-left order-1 lg:order-2">
                {/* Badges */}
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mb-6 animate-fade-in">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-blue-500/10 dark:from-cyan-500/20 dark:to-blue-500/20 border border-cyan-500/20 dark:border-cyan-400/20">
                    <Sparkles className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                    <span className="text-sm font-medium text-cyan-700 dark:text-cyan-300">Enterprise Inventory</span>
                  </div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <path d="M15.337 3.415c-.282-.085-.574.106-.656.388l-.074.25c-.328 1.107-.573 1.795-1.283 2.13-.085.039-.178.067-.27.092l-.035.01-.052-.071c-.748-1.032-1.603-1.542-2.756-1.38-.865.121-1.64.6-2.19 1.32-.611.799-.936 1.857-.936 2.984 0 .908.223 1.717.564 2.374l-.008-.014c.371.737.882 1.32 1.494 1.662.516.288 1.078.418 1.633.375.42-.032.819-.155 1.174-.363.093.173.196.339.31.494.573.779 1.363 1.249 2.224 1.397.115.02.231.033.348.04v6.397l-7.826 1.73V5.485l.01-.003 4.015-1.101c.52-.143.965-.272 1.235-.372l.091-.034c.271-.102.456-.282.456-.498 0-.259-.277-.453-.658-.453-.071 0-.14.007-.206.02l-.082.017-.018.004-4.826 1.324-.092.025-.121.033c-.66.18-1.119.306-1.119.306v.001L2 5.486v17.027l11.653 2.487v-6.832c.45-.059.884-.21 1.268-.456.6-.385 1.05-.966 1.295-1.702.244-.732.26-1.554.06-2.335l-.005-.019.002-.001c-.203-.802-.633-1.495-1.233-1.943-.397-.296-.844-.483-1.32-.556v-.01c.007 0 .013-.002.02-.003.71-.093 1.386-.418 1.913-.944.609-.61.963-1.42 1.018-2.3.004-.06.007-.12.007-.18v-.025l.001-.212c0-1.096-.271-2.088-.747-2.812-.318-.483-.716-.86-1.163-1.107l-.007-.004.224-.753c.082-.28-.053-.57-.332-.654l-.003-.001zM12.5 11.96c.315.115.563.36.722.663.173.329.23.722.152 1.105-.078.381-.276.712-.563.933-.307.236-.702.354-1.122.305-.44-.052-.841-.285-1.12-.629-.278-.342-.42-.776-.38-1.204.04-.417.223-.795.525-1.066.294-.264.675-.403 1.076-.377.252.016.495.095.71.27zm-1.327-4.858c.308-.032.604.062.841.254.225.182.384.442.453.729.071.292.048.608-.074.88-.119.264-.326.472-.587.594-.176.082-.366.12-.556.113-.265-.01-.52-.108-.72-.289-.2-.181-.337-.428-.378-.699-.043-.285.013-.581.168-.831.195-.314.505-.516.853-.551z" fill="#95BF47"/>
                    </svg>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Shopify Partner</span>
                  </div>
                </div>

                {/* Main Headline */}
                <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-slate-900 dark:text-white mb-6 leading-[1.1]">
                  <span className="block">{t('welcome.title1')}</span>
                  <span className="relative inline-block">
                    <span className="bg-gradient-to-r from-cyan-600 via-blue-600 to-blue-700 dark:from-cyan-400 dark:via-blue-400 dark:to-blue-500 bg-clip-text text-transparent">
                      {t('welcome.title2')}
                    </span>
                    <svg className="absolute -bottom-2 left-0 w-full h-3 text-cyan-500/30 dark:text-cyan-400/30" viewBox="0 0 200 8" preserveAspectRatio="none">
                      <path d="M0 7 Q50 0 100 4 T200 2" stroke="currentColor" strokeWidth="3" fill="none" />
                    </svg>
                  </span>
                </h1>

                {/* Subtitle */}
                <p className="text-lg sm:text-xl lg:text-xl xl:text-2xl text-slate-600 dark:text-slate-300 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                  {t('welcome.subtitle')}
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-10">
                  <Button
                    onClick={() => setShowAuth(true)}
                    variant="primary"
                    className="w-full sm:w-auto bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white px-8 py-4 text-lg font-semibold shadow-xl shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300 hover:scale-[1.02]"
                  >
                    <Zap className="h-5 w-5 mr-2" />
                    {t('welcome.accessSystem')}
                  </Button>
                  <a
                    href="#features"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-semibold text-slate-700 dark:text-slate-200 bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl transition-all duration-300 hover:scale-[1.02]"
                  >
                    Learn More
                    <ChevronRight className="h-5 w-5" />
                  </a>
                </div>

                {/* Trust Indicators */}
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 sm:gap-6 text-slate-500 dark:text-slate-400">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-sm font-medium">SOC 2 Ready</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Lock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium">256-bit Encryption</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                    <span className="text-sm font-medium">99.9% Uptime</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid Section */}
        <section id="features" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className={`group relative p-6 sm:p-8 rounded-2xl bg-white/60 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200/60 dark:border-slate-700/60 hover:border-cyan-500/30 dark:hover:border-cyan-400/30 transition-all duration-500 hover:shadow-2xl hover:shadow-cyan-500/10 dark:hover:shadow-cyan-500/5 ${
                    activeFeature === index ? 'ring-2 ring-cyan-500/30 shadow-xl shadow-cyan-500/10' : ''
                  }`}
                  onMouseEnter={() => setActiveFeature(index)}
                >
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/5 to-blue-500/5 dark:from-cyan-500/10 dark:to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative">
                    <div className={`inline-flex p-3 sm:p-4 rounded-xl bg-gradient-to-br ${feature.color} shadow-lg mb-5`}>
                      <feature.icon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                    </div>

                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-3">
                      {t(feature.titleKey)}
                    </h3>

                    <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed">
                      {t(feature.descKey)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Metrics Section */}
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 dark:from-slate-800 dark:via-slate-900 dark:to-black p-8 sm:p-12 lg:p-16">
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px]" />
              <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-cyan-500/20 to-blue-500/10 rounded-full blur-[100px]" />

              <div className="relative">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                  {/* Metrics Grid */}
                  <div className="grid grid-cols-2 gap-4 sm:gap-6">
                    {[
                      { value: '85%', label: t('welcome.metrics.stockErrors'), icon: TrendingUp },
                      { value: '3x', label: t('welcome.metrics.processing'), icon: Zap },
                      { value: '92%', label: t('welcome.metrics.satisfaction'), icon: Award },
                      { value: '5hrs', label: t('welcome.metrics.timeSaved'), icon: Clock }
                    ].map((metric, index) => (
                      <div key={index} className="group p-5 sm:p-6 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/10 transition-all duration-300">
                        <metric.icon className="h-5 w-5 text-cyan-400 mb-3" />
                        <div className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent mb-2">
                          {metric.value}
                        </div>
                        <div className="text-sm text-slate-400">{metric.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Capabilities List */}
                  <div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-8">
                      {t('welcome.everything')}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        'welcome.features.multi',
                        'welcome.features.automated',
                        'welcome.features.supplier',
                        'welcome.features.custom',
                        'welcome.features.mobile',
                        'welcome.features.api'
                      ].map((itemKey, index) => (
                        <div key={index} className="flex items-center gap-3 group">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                            <Check className="h-3.5 w-3.5 text-emerald-400" />
                          </div>
                          <span className="text-sm sm:text-base text-slate-200 group-hover:text-white transition-colors">
                            {t(itemKey)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Shopify Integration Section */}
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#96bf48]/10 via-[#5e8e3e]/5 to-transparent dark:from-[#96bf48]/20 dark:via-[#5e8e3e]/10 dark:to-slate-900/50 border border-[#96bf48]/20 dark:border-[#96bf48]/30 p-8 sm:p-12 lg:p-16">
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(150,191,72,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(150,191,72,0.03)_1px,transparent_1px)] bg-[size:30px_30px]" />
              <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-[#96bf48]/20 to-[#5e8e3e]/10 rounded-full blur-[100px]" />

              <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                {/* Left Side - Content */}
                <div>
                  <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm mb-6">
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <path d="M15.337 3.415c-.282-.085-.574.106-.656.388l-.074.25c-.328 1.107-.573 1.795-1.283 2.13-.085.039-.178.067-.27.092l-.035.01-.052-.071c-.748-1.032-1.603-1.542-2.756-1.38-.865.121-1.64.6-2.19 1.32-.611.799-.936 1.857-.936 2.984 0 .908.223 1.717.564 2.374l-.008-.014c.371.737.882 1.32 1.494 1.662.516.288 1.078.418 1.633.375.42-.032.819-.155 1.174-.363.093.173.196.339.31.494.573.779 1.363 1.249 2.224 1.397.115.02.231.033.348.04v6.397l-7.826 1.73V5.485l.01-.003 4.015-1.101c.52-.143.965-.272 1.235-.372l.091-.034c.271-.102.456-.282.456-.498 0-.259-.277-.453-.658-.453-.071 0-.14.007-.206.02l-.082.017-.018.004-4.826 1.324-.092.025-.121.033c-.66.18-1.119.306-1.119.306v.001L2 5.486v17.027l11.653 2.487v-6.832c.45-.059.884-.21 1.268-.456.6-.385 1.05-.966 1.295-1.702.244-.732.26-1.554.06-2.335l-.005-.019.002-.001c-.203-.802-.633-1.495-1.233-1.943-.397-.296-.844-.483-1.32-.556v-.01c.007 0 .013-.002.02-.003.71-.093 1.386-.418 1.913-.944.609-.61.963-1.42 1.018-2.3.004-.06.007-.12.007-.18v-.025l.001-.212c0-1.096-.271-2.088-.747-2.812-.318-.483-.716-.86-1.163-1.107l-.007-.004.224-.753c.082-.28-.053-.57-.332-.654l-.003-.001zM12.5 11.96c.315.115.563.36.722.663.173.329.23.722.152 1.105-.078.381-.276.712-.563.933-.307.236-.702.354-1.122.305-.44-.052-.841-.285-1.12-.629-.278-.342-.42-.776-.38-1.204.04-.417.223-.795.525-1.066.294-.264.675-.403 1.076-.377.252.016.495.095.71.27zm-1.327-4.858c.308-.032.604.062.841.254.225.182.384.442.453.729.071.292.048.608-.074.88-.119.264-.326.472-.587.594-.176.082-.366.12-.556.113-.265-.01-.52-.108-.72-.289-.2-.181-.337-.428-.378-.699-.043-.285.013-.581.168-.831.195-.314.505-.516.853-.551z" fill="#95BF47"/>
                    </svg>
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Shopify Partner</span>
                  </div>

                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                    Seamless Shopify Sync
                  </h2>
                  <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 mb-8 leading-relaxed">
                    Connect your Shopify store and keep your inventory perfectly synchronized. Real-time updates, automatic stock adjustments, and unified product management across all channels.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    {[
                      { icon: Check, text: 'Real-time inventory sync' },
                      { icon: Check, text: 'Automatic stock updates' },
                      { icon: Check, text: 'Product data import' },
                      { icon: Check, text: 'Multi-location support' },
                      { icon: Check, text: 'Order tracking integration' },
                      { icon: Check, text: 'SKU mapping' }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#96bf48]/20 flex items-center justify-center">
                          <item.icon className="h-3.5 w-3.5 text-[#5e8e3e] dark:text-[#96bf48]" />
                        </div>
                        <span className="text-sm sm:text-base text-slate-700 dark:text-slate-200">{item.text}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={() => setShowAuth(true)}
                    variant="primary"
                    className="bg-[#96bf48] hover:bg-[#7ea23d] text-white px-6 py-3 text-base font-semibold shadow-lg shadow-[#96bf48]/25 hover:shadow-[#96bf48]/40 transition-all duration-300"
                  >
                    Connect Your Store
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                </div>

                {/* Right Side - Shopify Logo */}
                <div className="flex justify-center lg:justify-end">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#96bf48]/30 to-[#5e8e3e]/20 rounded-full blur-[60px] scale-125" />
                    <div className="relative bg-white dark:bg-slate-800 rounded-3xl p-8 sm:p-12 shadow-2xl shadow-[#96bf48]/10 border border-[#96bf48]/20">
                      {/* Shopify Logo SVG */}
                      <svg className="w-40 h-40 sm:w-56 sm:h-56 lg:w-64 lg:h-64" viewBox="0 0 109.5 124.5" fill="none">
                        <path d="M95.9 25.8c-.1-.6-.6-1-1.1-1-.5 0-9.3-.2-9.3-.2s-7.4-7.2-8.1-7.9c-.8-.8-2.2-.5-2.8-.4-.1 0-1.5.5-3.9 1.2-2.3-6.7-6.4-12.8-13.6-12.8h-.6c-2.1-2.7-4.6-3.9-6.8-3.9-16.8 0-24.8 21-27.4 31.7-6.7 2.1-11.5 3.5-12 3.7-3.7 1.2-3.8 1.3-4.3 4.8-.4 2.6-10 77-10 77l75.5 13.1V25.9c-.2 0-.5-.1-.6-.1zm-26.8-6.9c-1.9.6-4 1.2-6.3 2V19c0-2.9-.4-5.3-1-7.3 2.5.4 4.2 3.2 5.4 6.5l1.9.7zm-9.9-5.5c.7 2 1.1 4.8 1.1 8.6v.6l-13 4c2.5-9.6 7.2-14.3 11.9-13.2zm-5.1-6.1c.8 0 1.5.3 2.3.8-5.7 2.7-11.8 9.4-14.4 22.8l-10.3 3.2c2.9-9.6 9.7-26.8 22.4-26.8z" fill="#95bf47"/>
                        <path d="M94.8 24.8c-.5 0-9.3-.2-9.3-.2s-7.4-7.2-8.1-7.9c-.3-.3-.6-.4-1-.4v113l37.6-9.4S95.4 25.5 95.3 25c-.1-.6-.5-1.1-1.1-1.2-.5 0-.6 0-.6 0l1.2 1z" fill="#5e8e3e"/>
                        <path d="M57.3 40.7l-4.4 13s-3.8-2-8.5-2c-6.9 0-7.2 4.3-7.2 5.4 0 5.9 15.4 8.2 15.4 22.1 0 10.9-6.9 18-16.2 18-11.2 0-16.9-7-16.9-7l3-9.9s5.9 5.1 10.9 5.1c3.3 0 4.6-2.6 4.6-4.5 0-7.7-12.6-8.1-12.6-20.8 0-10.7 7.7-21.1 23.2-21.1 6 .1 8.7 1.7 8.7 1.7z" fill="#fff"/>
                      </svg>

                      {/* Sync Animation Indicator */}
                      <div className="absolute -bottom-3 -right-3 flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-700 rounded-full shadow-lg border border-slate-200 dark:border-slate-600">
                        <div className="relative flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#96bf48] opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-[#96bf48]"></span>
                        </div>
                        <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">Syncing</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Hardware Compatibility Section */}
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                {t('welcome.integrations.title')}
              </h2>
              <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                {t('welcome.integrations.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {[
                {
                  icon: Scan,
                  titleKey: 'welcome.integrations.scanner.title',
                  descKey: 'welcome.integrations.scanner.desc',
                  gradient: 'from-blue-500 to-cyan-500',
                  features: [
                    { icon: Usb, label: 'USB' },
                    { icon: Bluetooth, label: 'Bluetooth' },
                    { icon: Wifi, label: 'WiFi' },
                    { icon: Smartphone, label: 'Camera' }
                  ]
                },
                {
                  icon: Radio,
                  titleKey: 'welcome.integrations.liveTracking.title',
                  descKey: 'welcome.integrations.liveTracking.desc',
                  gradient: 'from-emerald-500 to-teal-500'
                },
                {
                  icon: Bell,
                  titleKey: 'welcome.integrations.notifications.title',
                  descKey: 'welcome.integrations.notifications.desc',
                  gradient: 'from-amber-500 to-orange-500'
                },
                {
                  icon: Monitor,
                  titleKey: 'welcome.integrations.crossPlatform.title',
                  descKey: 'welcome.integrations.crossPlatform.desc',
                  gradient: 'from-blue-600 to-blue-800'
                }
              ].map((integration, index) => (
                <div
                  key={index}
                  className="group relative p-6 sm:p-8 rounded-2xl bg-white/60 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200/60 dark:border-slate-700/60 hover:border-cyan-500/30 dark:hover:border-cyan-400/30 transition-all duration-500 hover:shadow-2xl hover:shadow-cyan-500/10"
                >
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${integration.gradient} shadow-lg mb-5`}>
                    <integration.icon className="h-6 w-6 text-white" />
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-3">
                    {t(integration.titleKey)}
                  </h3>

                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                    {t(integration.descKey)}
                  </p>

                  {integration.features && (
                    <div className="flex flex-wrap gap-2">
                      {integration.features.map((feature, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-100 dark:bg-slate-700/50 rounded-lg"
                        >
                          <feature.icon className="h-3.5 w-3.5 text-cyan-600 dark:text-cyan-400" />
                          <span className="text-xs font-medium text-slate-700 dark:text-slate-200">{feature.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Demo Section */}
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl bg-white/60 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200/60 dark:border-slate-700/60 p-8 sm:p-12 lg:p-16">
              <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-br from-cyan-500/10 to-blue-500/5 rounded-full blur-[60px]" />
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-500/10 to-cyan-500/5 rounded-full blur-[80px]" />

              <div className="relative">
                <div className="text-center mb-10 sm:mb-12">
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                    {t('welcome.demo.title')}
                  </h2>
                  <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                    {t('welcome.demo.subtitle')}
                  </p>
                </div>

                <div className="max-w-4xl mx-auto">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/20 dark:shadow-black/40 border border-slate-200/50 dark:border-slate-700/50">
                    <img
                      src="https://utrprngboxriryrgetkp.supabase.co/storage/v1/object/public/mylogo/getinv_qr_dark_strongglow.gif"
                      alt="Barcode Scanning Demo"
                      className="w-full h-full object-contain bg-slate-900"
                    />

                    <div className="absolute top-4 right-4">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/90 backdrop-blur-sm rounded-full">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                        <span className="text-xs font-semibold text-white">LIVE</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
                    {[
                      { icon: Zap, label: 'Instant Recognition', color: 'text-amber-600 dark:text-amber-400' },
                      { icon: Check, label: 'Auto-Update Inventory', color: 'text-emerald-600 dark:text-emerald-400' },
                      { icon: Bell, label: 'Real-Time Notifications', color: 'text-blue-600 dark:text-blue-400' }
                    ].map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-4 bg-white/80 dark:bg-slate-800/80 rounded-xl border border-slate-200/50 dark:border-slate-700/50">
                        <feature.icon className={`h-5 w-5 flex-shrink-0 ${feature.color}`} />
                        <span className="text-sm font-medium text-slate-900 dark:text-white">{feature.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Package Tracking Section */}
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                {t('welcome.tracking.title')}
              </h2>
              <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                {t('welcome.tracking.subtitle')}
              </p>
            </div>

            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 p-8 sm:p-12 lg:p-16">
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />

              <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                {/* Tracking Card */}
                <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700">
                      <Truck className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mb-1">TRACKING NUMBER</p>
                      <p className="text-lg font-bold text-slate-900 dark:text-white">1Z999AA10123456784</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {[
                      { status: 'Delivered', time: 'Today, 2:45 PM', icon: Check, active: true },
                      { status: 'Out for Delivery', time: 'Today, 10:30 AM', icon: Truck, active: false },
                      { status: 'In Transit', time: 'Yesterday, 3:15 PM', icon: MapPin, active: false },
                      { status: 'Shipped', time: '2 days ago', icon: Package, active: false }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-4">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                          item.active
                            ? 'bg-emerald-100 dark:bg-emerald-900/40'
                            : 'bg-slate-100 dark:bg-slate-700'
                        }`}>
                          <item.icon className={`h-5 w-5 ${
                            item.active
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-slate-500 dark:text-slate-400'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-semibold ${
                            item.active
                              ? 'text-emerald-700 dark:text-emerald-300'
                              : 'text-slate-900 dark:text-white'
                          }`}>{item.status}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{item.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                        <Globe className="h-4 w-4" />
                        <span className="text-sm">UPS, FedEx, DHL, USPS</span>
                      </div>
                      <button className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
                        <Share2 className="h-4 w-4" />
                        <span className="text-sm font-medium">Share</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Features List */}
                <div className="space-y-6">
                  {[
                    { icon: Truck, text: t('welcome.tracking.autoTracking'), gradient: 'from-blue-500 to-cyan-500' },
                    { icon: MapPin, text: t('welcome.tracking.realtimeUpdates'), gradient: 'from-emerald-500 to-teal-500' },
                    { icon: Bell, text: t('welcome.tracking.notifications'), gradient: 'from-amber-500 to-orange-500' },
                    { icon: Share2, text: t('welcome.tracking.customerShare'), gradient: 'from-blue-600 to-blue-800' }
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-4 group">
                      <div className={`flex-shrink-0 p-3 rounded-xl bg-gradient-to-br ${feature.gradient} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <feature.icon className="h-5 w-5 text-white" />
                      </div>
                      <p className="text-base sm:text-lg font-medium text-white pt-2">
                        {feature.text}
                      </p>
                    </div>
                  ))}

                  <div className="mt-8 pt-6 border-t border-white/10">
                    <p className="text-sm text-slate-400 mb-3">Supported carriers:</p>
                    <div className="flex flex-wrap gap-2">
                      {['UPS', 'FedEx', 'DHL', 'USPS', 'PostNL', 'DPD', 'TNT', 'Aramex'].map((carrier) => (
                        <span key={carrier} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full text-sm font-medium text-white transition-colors">
                          {carrier}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technology Stack Section */}
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              {[
                { icon: Shield, title: t('welcome.features.security.title'), desc: t('welcome.tech.security'), gradient: 'from-slate-600 to-slate-800' },
                { icon: Zap, title: 'React + TypeScript', desc: t('welcome.tech.react'), gradient: 'from-cyan-500 to-blue-600' },
                { icon: Cloud, title: 'Progressive Web App', desc: t('welcome.tech.pwa'), gradient: 'from-blue-500 to-blue-700' }
              ].map((tech, index) => (
                <div key={index} className="group p-6 sm:p-8 rounded-2xl bg-white/60 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200/60 dark:border-slate-700/60 hover:border-cyan-500/30 dark:hover:border-cyan-400/30 transition-all duration-500 text-center">
                  <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${tech.gradient} shadow-lg mb-5`}>
                    <tech.icon className="h-7 w-7 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{tech.title}</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{tech.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* System Status Section */}
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl bg-white/60 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200/60 dark:border-slate-700/60 p-8 sm:p-12">
              <div className="text-center mb-10">
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-2">
                  {t('welcome.systemStatus')}
                </h3>
                <p className="text-slate-600 dark:text-slate-400">All systems operational</p>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10">
                {[
                  { icon: Database, label: t('welcome.status.database') },
                  { icon: Shield, label: t('welcome.status.security') },
                  { icon: Server, label: t('welcome.status.analytics') },
                  { icon: Layers, label: t('welcome.status.scanning') }
                ].map((status, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 sm:p-5 bg-slate-50/80 dark:bg-slate-900/50 rounded-xl border border-slate-200/50 dark:border-slate-700/50">
                    <status.icon className="h-5 w-5 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{status.label}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="text-xs text-emerald-600 dark:text-emerald-400">{t('welcome.status.operational')}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Support Section */}
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 sm:p-10 lg:p-12">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(6,182,212,0.15),transparent_50%),radial-gradient(ellipse_at_bottom_left,rgba(59,130,246,0.1),transparent_50%)]" />
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-cyan-500/10 to-transparent rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-blue-500/10 to-transparent rounded-full blur-3xl" />

                <div className="relative">
                  <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                    <div className="flex-1 text-center lg:text-left">
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-4">
                        <Heart className="h-4 w-4 text-cyan-400" />
                        <span className="text-xs font-medium text-cyan-300 uppercase tracking-wider">Community Supported</span>
                      </div>

                      <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                        Help Us Keep Improving
                      </h3>

                      <p className="text-base sm:text-lg text-slate-300 mb-4 max-w-xl">
                        We are constantly working to make GetInv better. Your donations go directly toward developing new features, improving stability, and delivering the best experience possible.
                      </p>

                      <p className="text-sm text-slate-400">
                        Every contribution helps us grow and serve you better. Thank you for your support!
                      </p>
                    </div>

                    <div className="flex-shrink-0">
                      <a
                        href="https://www.paypal.com/donate/?hosted_button_id=L8ZMCH99B76UQ"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-semibold rounded-2xl shadow-xl shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300 hover:scale-105 hover:-translate-y-0.5"
                      >
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-400 to-blue-400 opacity-0 group-hover:opacity-100 blur transition-opacity duration-300" />
                        <Heart className="relative h-5 w-5 group-hover:scale-110 transition-transform fill-white/20" />
                        <span className="relative">Support with a Donation</span>
                      </a>
                      <p className="text-center text-xs text-slate-500 mt-3">Secure payment via PayPal</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="relative py-16 sm:py-20 px-4 sm:px-6 lg:px-8 border-t border-slate-200/50 dark:border-slate-800/50 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-100/50 to-slate-100 dark:via-slate-900/50 dark:to-slate-900" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[200px] bg-gradient-to-b from-cyan-500/5 to-transparent rounded-full blur-3xl" />

          <div className="relative max-w-7xl mx-auto">
            <div className="flex flex-col items-center text-center">
              <div className="mb-8 p-4 rounded-2xl bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm border border-slate-200/50 dark:border-slate-700/50 shadow-lg shadow-slate-900/5 dark:shadow-black/20">
                <img
                  src="/ChatGPT Image Oct 8, 2025, 03_35_44 AM.png"
                  alt="GetInv"
                  className="h-20 sm:h-24 w-auto"
                />
              </div>

              <h4 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-2">
                Professional Inventory Management
              </h4>

              <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-md">
                {t('welcome.footer.built')}
              </p>

              <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
                <a
                  href="https://www.getinv.app"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 hover:border-cyan-500 hover:text-cyan-600 dark:hover:text-cyan-400 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <Globe className="h-4 w-4" />
                  www.getinv.app
                </a>
                <a
                  href="mailto:support@getinv.app"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 hover:border-cyan-500 hover:text-cyan-600 dark:hover:text-cyan-400 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <Mail className="h-4 w-4" />
                  support@getinv.app
                </a>
              </div>

              <div className="w-full max-w-xs h-px bg-gradient-to-r from-transparent via-slate-300 dark:via-slate-700 to-transparent mb-6" />

              <div className="flex flex-col sm:flex-row items-center gap-2 text-xs text-slate-400 dark:text-slate-500">
                <span>{new Date().getFullYear()} GetInv. All rights reserved.</span>
                <span className="hidden sm:block">|</span>
                <span>{t('welcome.footer.creator')}</span>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Authentication Modal */}
      {showAuth && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-2xl shadow-slate-900/20 dark:shadow-black/40 border border-slate-200/50 dark:border-slate-700/50 p-8">
            {/* Close Button */}
            <button
              onClick={() => setShowAuth(false)}
              className="absolute top-4 right-4 p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <span className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-2xl leading-none">&times;</span>
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex p-3 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/25 mb-4">
                <Lock className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {isLogin ? t('auth.welcome') : t('auth.createAccount')}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Enter your credentials to continue
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <Input
                  label={t('auth.fullName')}
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder={t('auth.enterFullName')}
                  required
                />
              )}

              <Input
                label={t('auth.email')}
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('auth.enterEmail')}
                required
              />

              <div className="relative">
                <Input
                  label={t('auth.password')}
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={t('auth.enterPassword')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-slate-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-slate-400" />
                  )}
                </button>
              </div>

              {error && (
                <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                    <span className="text-red-600 dark:text-red-400 text-lg">!</span>
                  </div>
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                loading={loading}
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white py-3.5 text-base font-semibold shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/30 transition-all duration-300"
              >
                {isLogin ? t('auth.login') : t('auth.createAccount')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              {isLogin ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Don't have an account?{' '}
                  <a
                    href="mailto:support@getinv.app"
                    className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                  >
                    Contact us
                  </a>
                </p>
              ) : (
                <button
                  onClick={() => {
                    setIsLogin(true);
                    setError('');
                  }}
                  className="text-sm text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                >
                  {t('auth.haveAccount')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
